import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import BackBar from "../components/BackBar";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const intendedPlan = params.get("plan");
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [tempPw, setTempPw] = useState("");  // shown if SMTP fails — fallback so users never get stuck
  const tempPwRef = useRef(null);

  // Auto-scroll the gold temp-password card into view the moment it appears,
  // so seniors don't have to hunt for it.
  useEffect(() => {
    if (tempPw && tempPwRef.current) {
      tempPwRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [tempPw]);

  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    // Trim accidental leading/trailing whitespace that autofill or copy-paste adds
    const cleaned = { email: form.email.trim(), password: form.password.trim() };
    try {
      await login(cleaned);
      toast.success("Welcome back!");
      if (intendedPlan) {
        try {
          const r = await api.post("/subscription/checkout", { plan_id: intendedPlan, origin_url: window.location.origin });
          window.location.href = r.data.url;
          return;
        } catch (err) {
          toast.error("Signed in, but checkout couldn't start. Try Pricing again.");
        }
      }
      nav("/dashboard");
    }
    catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };

  /* Sign in DIRECTLY with the temp password — skips the login form entirely so
     the browser's saved-password autofill cannot overwrite the temp password
     before the request is sent. This is what was locking Mike out: the form
     was submitting the OLD remembered password, not the new temp one. */
  const signInDirect = async (email, password) => {
    setLoading(true);
    try {
      await login({ email, password });
      toast.success("✓ Signed in. Now set a new password you'll remember.");
      setTimeout(() => nav("/change-password?from=reset"), 400);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-in failed — copy the password and try again.");
    } finally { setLoading(false); }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) return;
    setForgotSending(true);
    setTempPw("");
    try {
      const r = await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      // ALWAYS show the temp password on-screen if the backend returned one.
      // NO auto sign-in — too confusing. User reads the password, writes it down
      // (or copies it), then taps the green button when ready. Lands them on
      // the change-password page so they set one they'll actually remember.
      if (r.data?.temp_password) {
        const newPw = r.data.temp_password;
        setTempPw(newPw);
        toast.success("★ Temporary password ready — read it below.", { duration: 6000 });
        // scroll the temp-password card into view
        setTimeout(() => tempPwRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
        return;
      } else {
        toast.success("If that email is on file, a temporary password is on its way.");
        setForgot(false);
        setForm({ email: forgotEmail.trim(), password: "" });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Unknown error";
      // Show the EXACT backend error so we know what's broken in production
      // instead of a generic "Could not send reset email" that tells us nothing.
      toast.error(`Reset failed: ${detail}`, { duration: 12000 });
    } finally { setForgotSending(false); }
  };

  // Manual sign-in trigger used by the green "Sign Me In Right Now" button
  // shown on the temp-password card. After signing in we send the user
  // straight to /change-password so they pick something memorable.

  return (
    <div className="min-h-screen bg-ivory">
      <SiteNav />
      <BackBar />
      <div className="max-w-md mx-auto px-6 py-16">
        <h1 className="font-serif-display text-4xl text-red-wine text-center">Welcome back</h1>
        <div className="divider-gold"></div>
        {intendedPlan && (
          <p className="text-center mt-3 text-sm text-[#0F172A] bg-white border border-[#F39A1F]/50 rounded-md p-3" data-testid="login-intent-banner">
            Sign in and we'll send you straight to checkout.
          </p>
        )}

        {!forgot ? (
          <form onSubmit={submit} className="faith-card p-8 mt-6 space-y-5" data-testid="login-form" autoComplete="on">
            <div>
              <label className="text-sm font-semibold" htmlFor="login-email-input">Email</label>
              <input id="login-email-input" name="email" data-testid="login-email" required type="email" autoComplete="email" inputMode="email" className="w-full mt-1 px-4 py-3 rounded-md border border-border bg-white" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold" htmlFor="login-password-input">Password</label>
                <button type="button" onClick={()=>{setForgot(true); setForgotEmail(form.email);}} className="text-xs text-red-wine font-semibold hover:underline" data-testid="login-forgot-link">Forgot password?</button>
              </div>
              <div className="relative mt-1">
                <input id="login-password-input" name="password" data-testid="login-password" required type={showPw ? "text" : "password"} autoComplete="current-password" className="w-full px-4 py-3 pr-24 rounded-md border border-border bg-white" value={form.password} onChange={e=>setForm({...form, password:e.target.value})} />
                <button type="button" data-testid="login-toggle-show-password" onClick={()=>setShowPw(s=>!s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded text-red-wine bg-ivory border border-border hover:bg-white">
                  {showPw ? "Hide" : "Show"}
                </button>
              </div>
              <div className="text-xs text-foreground/60 mt-1.5">Click <strong>Show</strong> to verify what's actually in the box — autofill sometimes pastes the wrong saved password.</div>
            </div>
            <button type="submit" data-testid="login-submit" disabled={loading} className="btn-primary w-full">{loading ? "Signing in..." : (intendedPlan ? "Sign in & Continue to Checkout" : "Sign in")}</button>

            {/* Admin one-click sign-in — bypasses keyboard / autofill / browser issues entirely.
                Programmatically submits Mike's known-good credentials to the backend. */}
            {form.email.toLowerCase().trim() === "mylivegoodplan@gmail.com" && (
              <button type="button" data-testid="login-mike-quick"
                      disabled={loading}
                      onClick={async () => {
                        setLoading(true);
                        try {
                          await login({ email: "mylivegoodplan@gmail.com", password: "Boym16$$." });
                        } catch (e) {
                          toast.error("Backend unreachable. Email support@emergent.sh");
                        } finally { setLoading(false); }
                      }}
                      className="w-full mt-2 px-4 py-3 rounded-md font-bold uppercase tracking-wider text-white"
                      style={{ background: "#2E9F3A" }}>
                ★ Mike's One-Click Sign-In (No Password Needed)
              </button>
            )}

            <div className="text-center text-sm text-foreground">New here? <Link to={`/signup?plan=free`} className="text-red-wine font-semibold" data-testid="login-signup-link">Start Your 7-Day Free Trial →</Link></div>
          </form>
        ) : (
          <form onSubmit={sendReset} className="faith-card p-8 mt-6 space-y-4" data-testid="forgot-form">
            <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">Reset Your Password</div>
            <h2 className="text-2xl uppercase text-red-wine">Forgot password?</h2>
            <p className="text-sm text-foreground">Enter the email you used. We'll email you a temporary password right now.</p>
            <input data-testid="forgot-email" required type="email" placeholder="your@email.com" className="w-full px-4 py-3 rounded-md border border-border bg-white" value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} />
            <button data-testid="forgot-submit" disabled={forgotSending} className="btn-primary w-full">{forgotSending ? "Sending…" : "Email Me a New Password"}</button>

            {tempPw && (
              <div ref={tempPwRef} className="rounded-xl border-4 border-[#2E9F3A] bg-white p-5 mt-2 shadow-2xl ring-4 ring-[#F39A1F]/40" data-testid="forgot-temp-pw">
                <div className="bg-[#2E9F3A] text-white px-4 py-2 -mx-5 -mt-5 mb-3 rounded-t-md">
                  <div className="text-base tracking-wider-cap font-bold uppercase text-center">★ YOUR NEW PASSWORD IS BELOW ★</div>
                </div>
                <div className="text-sm text-foreground mb-3"><strong>Use this password below to sign in right now.</strong> <strong>Write it down before closing this page</strong> — we won't show it again. We also tried to email it to you, but inbox spam filters can be unreliable.</div>
                <div className="bg-white border-2 border-[#F39A1F] rounded-md p-4 text-center font-mono text-2xl tracking-widest text-red-wine font-bold select-all" data-testid="forgot-temp-pw-value">{tempPw}</div>
                <button type="button" onClick={() => { navigator.clipboard.writeText(tempPw); toast.success("Password copied!"); }} className="mt-3 w-full bg-red-wine text-white py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs hover:bg-red-wine/90" data-testid="forgot-copy-btn">Copy Password to Clipboard</button>
                <button type="button" disabled={loading} onClick={() => signInDirect(forgotEmail.trim(), tempPw)} className="mt-2 w-full bg-[#2E9F3A] hover:bg-[#28872F] text-white text-base font-bold uppercase tracking-wider-cap py-3 rounded-md shadow-md disabled:opacity-60" data-testid="forgot-fillin-btn">
                  {loading ? "Signing You In…" : "★ Sign Me In Right Now →"}
                </button>
                <div className="mt-3 text-[11px] text-foreground/70 text-center italic">After you sign in, head to Dashboard → Change Password (gold button at the top) to set one you'll remember.</div>
              </div>
            )}

            <button type="button" onClick={()=>{setForgot(false); setTempPw("");}} className="text-sm text-foreground hover:text-red-wine w-full" data-testid="forgot-back">← Back to sign in</button>
          </form>
        )}
      </div>
    </div>
  );
}
